import React, {useEffect, useState, useCallback} from 'react';
import {Grid, Typography, Button, Link, IconButton, Tooltip, TextField, CircularProgress} from '@material-ui/core';
import {alpha} from '@material-ui/core/styles/colorManipulator';
import {Alert, AlertTitle} from '@material-ui/lab';
import {useGoogleLogin, hasGrantedAnyScopeGoogle} from '@react-oauth/google';
import googleGLogo from '../../../components/img/Google__G__Logo.svg';
import adsenseLogo from '../../../components/img/adsense-logo.svg';
import useStore from '../../../store/store';
import {APICall} from '../../../api/api';
import {Skeleton} from '@material-ui/lab';
import {makeStyles} from '@material-ui/core/styles';
import {SiteStatus} from '../../helpers/MCMstatusDecoder';
import {NewDomainDialog} from './NewDomainDialog';
import {Launch} from '@material-ui/icons';
import useRefreshMenu from '../../../hooks/useRefreshMenu';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import useDomains from '../../../hooks/useDomains';
import usePersistentAuthStore from '../../../store/persistentAuthStore';

const useStyles = makeStyles(theme => ({
  listWrapper: {
    // maxHeight: 400,
    overflowX: 'hidden',
    borderRadius: 8,
  },
  itemWrapper: {
    padding: '10px 15px',
    border: '1px solid #7979793b',
    marginBottom: 15,
    borderRadius: 4,
    minHeight: 58,
    '&:hover': {
      background: theme.palette.graphColors.sorted,
    },
  },
  itemWrapperSelected: {
    border: '1px solid #7979793b',
    marginBottom: 15,
    minHeight: 58,
    borderRadius: 4,
    padding: '10px 15px',
    '&:hover': {
      background: theme.palette.graphColors.sorted,
    },
  },
  siteName: {
    fontWeight: 600,
  },
  hidden: {
    display: 'none',
  },
  domainImage: {
    height: 20,
    width: 20,
    position: 'relative',
    top: 5,
    backgroundColor: 'white',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '80%',
    borderRadius: 4,
    display: 'inline-block',
    marginRight: 15,
  },
  launchIcon: {
    opacity: 0.5,
    '&:hover': {
      opacity: 1,
    },
  },
  successIcon: {
    position: 'relative',
    marginLeft: 6,
    fontSize: 24,
  },
  errorTooltip: {
    maxWidth: 440,
    padding: 0,
    fontSize: 13,
    lineHeight: 1.5,
  },
  errorTooltipBody: {
    maxHeight: 200,
    overflowY: 'auto',
    padding: theme.spacing(1.5, 2),
    scrollbarWidth: 'thin',
    scrollbarColor: `${alpha('#fff', 0.28)} ${alpha('#000', 0.25)}`,
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: alpha('#fff', 0.22),
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: alpha('#000', 0.2),
      borderRadius: 4,
    },
  },
  errorTooltipMessage: {
    color: alpha(theme.palette.common.white, 0.92),
    fontSize: '0.8125rem',
    lineHeight: 1.45,
    wordBreak: 'break-word',
  },
  signInButton: {
    marginTop: theme.spacing(1.5),
    padding: '8px 20px',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: theme.shadows[2],
  },
  signInButtonIcon: {
    height: 18,
    width: 18,
    display: 'block',
  },
  errorAlertTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 600,
  },
  errorBrandLogo: {
    height: 22,
    display: 'block',
  },
}));

function formatSitesListError(rawError) {
  const error = rawError || 'Unknown error';

  if (error.includes('invalid_client') || error.includes('invalid_grant')) {
    return {
      title: 'Google authentication failed',
      description:
        'Your Google sign-in may have expired or is no longer valid. Sign in with Google again to refresh access.',
    };
  }

  if (error.includes('NO_NETWORKS_TO_ACCESS')) {
    return {
      title: 'No Ad Manager networks found',
      description:
        'Your Google sign-in may still be valid, but no Ad Manager networks were found for this account. Sign in with a different Google account if you expected networks to appear here.',
    };
  }

  if (error.includes('Authentication data not found')) {
    return {
      title: 'Google account not connected',
      description:
        'No Google authentication was found for this publisher. Sign in with Google to connect your Ad Manager account.',
    };
  }

  if (error.includes('grant access to Google Ad Manager')) {
    return {
      title: 'Google Ad Manager permission required',
      description: error,
    };
  }

  if (error.includes('API Access disabled')) {
    return {
      title: 'Ad Manager API access is disabled',
      description:
        'API access must be enabled in Google Ad Manager before sites can be fetched.',
      helpLink: 'https://support.google.com/admanager/answer/3088588?hl=en',
      helpText: 'How to turn on API access',
    };
  }

  if (error.includes('Permission denied')) {
    return {
      title: 'Permission denied',
      description:
        "Your account doesn't have permission to view and create sites on this network.",
      helpLink:
        'https://support.google.com/admanager/answer/9154293?hl=en#zippy=%2Cview-sites',
      helpText: 'Learn about site permissions',
    };
  }

  if (error.includes('Failed to import domain')) {
    return {
      title: 'Failed to import domain',
      description: 'The site could not be imported. Please try again later.',
    };
  }

  if (
    error.includes('AdSenseAccountError') ||
    error.includes('CANNOT_ACCESS_INVALID_ACCOUNT')
  ) {
    return {
      title: 'Your AdSense account needs attention',
      description:
        "We couldn't load your sites because the Google AdSense account connected to your Ad Manager setup isn't active. This usually happens when Google has disabled or suspended the account — for example, after a policy review. Check the email address you use for AdSense for a message from Google, then follow the steps to fix the issue or submit an appeal. Once your AdSense account is restored, come back here and try again. For more guidance,",
      brand: 'adsense',
      helpLink: 'https://support.google.com/adsense/answer/57153',
      helpText: "read Google's help article on disabled AdSense accounts",
      showSignInButton: false,
      showTechnicalDetails: false,
    };
  }

  return {
    title: 'Failed to fetch Ad Manager sites',
    description:
      'We could not load the sites list from Google Ad Manager. This is often caused by authentication or permission issues.',
  };
}

export function SitesList({publisher}) {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(null);
  const {refreshMenu} = useRefreshMenu();
  const {auth} = usePersistentAuthStore();
  const classes = useStyles();
  const [sites, setSites] = useState(null);
  const {domains} = useDomains();
  const publisherDomains = domains.filter(domain => domain.publisherId === publisher.publisherId);
  const [search, setSearch] = useState('');
  const sitesListErrorDisplay = error ? formatSitesListError(error) : null;

  const getSitesList = useCallback(async () => {
    setSites(null);
    setLoading(true);
    setError(null);
    setSearch('');

    let response = await APICall({
      endpoint: 'getSitesList',
      params: `${publisher.gam_auth_id}/${publisher.networkCode}`,
      options: {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      },
    });

    if (response.success) {
      setSites(response);
    } else {
      // Handle no networks error case
      setError(response.error);
    }

    setLoading(false);
  }, [auth, publisher]);

  const assignGoogleAuth = useCallback(
    async googleCode => {
      setLoading(true);
      setError(null);

      const assignAuth = await APICall({
        endpoint: 'assignSelfServeAuth',
        options: {
          method: 'post',
          body: JSON.stringify({
            googleCode,
            publisherId: publisher.publisherId,
            currency: publisher.currency,
          }),
          headers: {
            Authorization: `Bearer ${auth.token}`,
            'Cache-Control': 'max-age=0, no-cache',
            'Content-Type': 'application/json',
          },
        },
      });

      if (assignAuth.success) {
        await getSitesList();
      } else {
        setError(assignAuth.error);
        setLoading(false);
      }
    },
    [auth, getSitesList, publisher.publisherId, publisher.currency]
  );

  const googleLoginHook = useGoogleLogin({
    flow: 'auth-code',
    scope: 'profile email https://www.googleapis.com/auth/dfp',
    onSuccess: async tokenResponse => {
      if (hasGrantedAnyScopeGoogle(tokenResponse, 'https://www.googleapis.com/auth/admanager')) {
        assignGoogleAuth(tokenResponse);
      } else {
        setError(
          'You must grant access to Google Ad Manager to use Setupad self-serve. Please click on "Sign in with Google" again to agree with the required permissions.'
        );
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    getSitesList();
  }, [getSitesList]);

  const importDomain = async site => {
    setLoading(true);
    setError(null);
    try {
      let importDomain = await APICall({
        endpoint: 'importDomain',
        options: {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${auth.token}`,
            'Cache-Control': 'max-age=0, no-cache',
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            publisher_id: publisher.publisherId,
            url: site.url,
            siteId: site.id,
          }),
        },
      });

      if (!importDomain.success) {
        throw new Error(importDomain.error);
      } else {
        // Refresh menu
        refreshMenu();
      }
    } catch (e) {
      setError('Failed to import domain. Please try again later.');
    }
    setLoading(false);
  };

  return (
    <>
      <Grid item xs={12}>
        <Typography style={{fontWeight: 600}}>Sites list</Typography>
      </Grid>

      {sites?.success && !sites?.data && (
        <Grid item xs={12}>
          <Alert
            severity="info"
            action={
              <Button
                color="inherit"
                variant={'outlined'}
                style={{marginRight: 40, fontWeight: 600}}
                onClick={getSitesList}
              >
                Check again
              </Button>
            }
          >
            <AlertTitle>Selected network has no sites in Ad Manager inventory.</AlertTitle>
          </Alert>
        </Grid>
      )}

      {!sites && !error && (
        <Grid item xs={12}>
          <Skeleton height={100} variant="rect" style={{borderRadius: 4}} />
        </Grid>
      )}

      {sites?.success && sites?.data?.length && (
        <>
          {sites.data.length > 6 && (
            <Grid
              container
              //  className={classes.itemWrapper}
              justifyContent="center"
              alignItems="center"
              style={{padding: 8}}
            >
              <TextField
                fullWidth
                variant="filled"
                label="Search"
                onChange={({target: {value}}) => setSearch(value)}
                helperText={
                  sites.data.filter(site => (search.length > 0 ? site.url.includes(search) : true))
                    .length === 0
                    ? 'No results found'
                    : ''
                }
              />
            </Grid>
          )}
          <Grid item xs={12} className={classes.listWrapper}>
            {sites.data
              .filter(site => (search.length > 0 ? site.url.includes(search) : true))
              .map((site, i) => {
                const isImported = publisherDomains.find(domain => domain.siteId === site.id);
                return (
                  <Grid
                    key={i}
                    container
                    className={isImported ? classes.itemWrapperSelected : classes.itemWrapper}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Grid item xs={6}>
                      <Typography className={classes.siteName}>
                        <div
                          className={classes.domainImage}
                          style={{
                            backgroundImage: `url(https://www.google.com/s2/favicons?domain=https://${site.url}&sz=256)`,
                          }}
                        />
                        {site.url} <Typography variant="caption">({site.id})</Typography>
                        <Tooltip title={'Open in new tab'} arrow>
                          <IconButton href={`https://${site.url}`} target={'_blank'}>
                            <Launch className={classes.launchIcon} />
                          </IconButton>
                        </Tooltip>
                      </Typography>
                    </Grid>

                    <Grid item xs={3}>
                      <SiteStatus site={site} />
                    </Grid>
                    <Grid item xs={3}>
                      {!isImported && (
                        <Button
                          fullWidth
                          variant={'text'}
                          color={'primary'}
                          disabled={loading || publisher.order_generator_job_id}
                          onClick={() => importDomain(site)}
                        >
                          Import to Setupad
                        </Button>
                      )}
                      {isImported && (
                        <Button fullWidth variant={'text'} color={'primary'} disabled>
                          Imported{' '}
                          <CheckCircleIcon
                            fontSize={'inherit'}
                            color={'secondary'}
                            className={classes.successIcon}
                          />
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                );
              })}
          </Grid>
        </>
      )}

      {sites &&
        !error &&
        (!publisher.order_generator_job_id || publisher.gam_initialized === 1) && (
          <NewDomainDialog
            publisher={publisher}
            loading={loading}
            setLoading={setLoading}
            sites={sites.data}
            importDomain={importDomain}
          />
        )}

      {sitesListErrorDisplay && (
        <Grid item xs={12}>
          <Alert
            severity="warning"
            icon={
              sitesListErrorDisplay.brand === 'adsense' ? (
                <img
                  src={adsenseLogo}
                  alt="Google AdSense"
                  className={classes.errorBrandLogo}
                />
              ) : undefined
            }
          >
            <Typography variant="body2" component="div" className={classes.errorAlertTitle}>
              {sitesListErrorDisplay.title}
            </Typography>
            <Typography
              component="div"
              variant="body2"
              color="textSecondary"
              style={{marginTop: 4}}
            >
              {sitesListErrorDisplay.description}{' '}
              {sitesListErrorDisplay.helpLink && (
                <>
                  <Link href={sitesListErrorDisplay.helpLink} target={'_blank'} color="primary">
                    {sitesListErrorDisplay.helpText}
                  </Link>
                  .{' '}
                </>
              )}
              {sitesListErrorDisplay.showTechnicalDetails !== false && (
                <Tooltip
                  arrow
                  interactive
                  placement="bottom"
                  classes={{tooltip: classes.errorTooltip}}
                  title={
                    <div className={classes.errorTooltipBody}>
                      <Typography
                        variant="body2"
                        component="div"
                        className={classes.errorTooltipMessage}
                      >
                        {error}
                      </Typography>
                    </div>
                  }
                >
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    color="textSecondary"
                    underline="always"
                    aria-label="Show technical error details from Google Ad Manager"
                    style={{
                      cursor: 'pointer',
                      verticalAlign: 'baseline',
                      fontSize: 'inherit',
                      lineHeight: 'inherit',
                    }}
                  >
                    More details.
                  </Link>
                </Tooltip>
              )}
            </Typography>
            {sitesListErrorDisplay.showSignInButton !== false && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                className={classes.signInButton}
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <img
                      src={googleGLogo}
                      alt="Google"
                      className={classes.signInButtonIcon}
                    />
                  )
                }
                onClick={googleLoginHook}
                disabled={loading}
              >
                {loading ? 'Authenticating...' : 'Sign in with Google'}
              </Button>
            )}
          </Alert>
        </Grid>
      )}
    </>
  );
}
